"use client";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Input,
  Tag,
  Text,
  TextLabel,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@focal/ui-kit";
import { z } from "zod";

const emailSchema = z.string().trim().email("Enter a valid email address");

/** Maximum number of email addresses a client may carry (enforced client-side). */
export const MAX_EMAILS = 5;

const PRIMARY_EMAIL_TOOLTIP =
  "This is the primary email that will be used by Focal for drafting emails, and identifying meeting participants from calendar event invitees.";

/**
 * A locally-staged email row. `key` is a client-side identifier only (these
 * rows do not exist on the backend yet) — it is never sent.
 */
export interface EmailDraft {
  key: string;
  email: string;
  primary: boolean;
}

/** Create an empty draft row. The first row in a list is primary. */
export const newEmailDraft = (primary: boolean): EmailDraft => ({
  key: crypto.randomUUID(),
  email: "",
  primary,
});

/**
 * True when no row holds an invalid email. Empty rows are ignored and an empty
 * list is valid — email is optional (the modal separately requires at least one
 * contact method across emails + phones).
 */
export const areEmailDraftsValid = (drafts: EmailDraft[]): boolean =>
  drafts.every(
    (d) =>
      d.email.trim().length === 0 || emailSchema.safeParse(d.email).success,
  );

/** True when at least one row holds a valid email. */
export const hasValidEmail = (drafts: EmailDraft[]): boolean =>
  drafts.some(
    (d) => d.email.trim().length > 0 && emailSchema.safeParse(d.email).success,
  );

interface EmailDraftListProps {
  value: EmailDraft[];
  onChange: (next: EmailDraft[]) => void;
}

/**
 * Create-flow counterpart to {@link ClientEmailList}: an always-editable,
 * fully-local list of email drafts. Nothing is persisted here — the parent
 * modal reads the drafts and sends them as the `emailAddresses` array in the
 * single create request. Visuals mirror the edit list (primary tag, per-row
 * 3-dot menu, "Add email" affordance, max-5 guard) for consistency.
 *
 * Invariants enforced here: exactly one primary while any row has content, and
 * at least one row always present (create requires an email).
 */
export const EmailDraftList = ({ value, onChange }: EmailDraftListProps) => {
  const setEmail = (key: string, email: string) =>
    onChange(value.map((d) => (d.key === key ? { ...d, email } : d)));

  const makePrimary = (key: string) =>
    onChange(value.map((d) => ({ ...d, primary: d.key === key })));

  const addRow = () =>
    onChange([...value, newEmailDraft(value.length === 0)]);

  const removeRow = (key: string) => {
    const next = value.filter((d) => d.key !== key);
    // Promote a remaining row to primary if we removed the primary one.
    if (next.length > 0 && !next.some((d) => d.primary)) {
      next[0] = { ...next[0], primary: true };
    }
    onChange(next);
  };

  const rowError = (draft: EmailDraft): string | null => {
    if (draft.email.trim().length === 0) return null;
    return emailSchema.safeParse(draft.email).success
      ? null
      : "Enter a valid email address";
  };

  const renderPrimaryTag = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <Tag>Primary</Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{PRIMARY_EMAIL_TOOLTIP}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex flex-1 flex-col gap-2 max-w-[360px] min-w-[250px]">
      <TextLabel>Email</TextLabel>

      <div className="flex flex-col gap-2">
        {value.map((draft) => {
          const error = rowError(draft);
          return (
            <div
              key={draft.key}
              data-cy="client-email-draft-row"
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <Input
                  type="email"
                  value={draft.email}
                  data-cy="client-email-draft-input"
                  onChange={(event) => setEmail(draft.key, event.target.value)}
                  placeholder="email@example.com"
                />
                {error && (
                  <Text variant="p3" textColor="error">
                    {error}
                  </Text>
                )}
              </div>

              {draft.primary ? (
                renderPrimaryTag()
              ) : (
                // Reserve the primary-pill slot so every input stops at the
                // same right edge instead of growing into the empty space.
                <span aria-hidden className="invisible inline-flex shrink-0">
                  <Tag>Primary</Tag>
                </span>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="iconSm"
                    variant="ghostSecondary"
                    type="button"
                    aria-label="Email options"
                    data-cy="client-email-draft-menu"
                  >
                    <Icon name="MoreVertIcon" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!draft.primary && (
                    <DropdownMenuItem
                      data-cy="client-email-draft-make-primary"
                      onClick={() => makePrimary(draft.key)}
                    >
                      Make primary
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-cy="client-email-draft-remove"
                    onClick={() => removeRow(draft.key)}
                  >
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>

      {value.length >= MAX_EMAILS ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex w-fit">
              <Button
                size="sm"
                variant="link"
                type="button"
                data-cy="client-email-draft-add"
                disabled
                iconLeft={<Icon name="AddIcon" />}
              >
                Add email
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            You can add up to {MAX_EMAILS} email addresses.
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          variant="link"
          type="button"
          className="w-fit"
          data-cy="client-email-draft-add"
          iconLeft={<Icon name="AddIcon" />}
          onClick={addRow}
        >
          Add email
        </Button>
      )}
    </div>
  );
};
